/**
 * "Bexio-Kontakte (Vorschau)" — a few mirrored contacts with their mapping state.
 *
 * Order is the server's (`synced_at DESC`), so this is "most recently synced",
 * which is what the endpoint means. The card does not claim they are the newest
 * contacts.
 *
 * `display_name` can be a concatenated string covering several people (couples,
 * co-tenancies) — the model says so explicitly — so it is shown as-is and never
 * treated as one tenant.
 *
 * The mapping column comes from the batched status call the page already makes;
 * a contact the batch did not answer for shows "—" rather than a guess.
 *
 * No footer link: there is no contacts route yet.
 */
import React from "react";

import { MAPPING_STATUS_LABEL, mapLabel, mappingTone } from "../bexioFormat";
import BexioPreviewFooter from "./BexioPreviewFooter";

const TONE = {
  good:
    "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400",
  warn:
    "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300",
  bad:
    "border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300",
  neutral: "border-border bg-muted text-muted-foreground",
};

export default function BexioContactsPreview({
  contacts,
  mappingStatuses,
  total,
  onLoadMore,
  loadingMore,
}) {
  const rows = Array.isArray(contacts) ? contacts : [];
  const statuses = mappingStatuses || {};

  return (
    <section
      aria-labelledby="bexio-contacts-preview-title"
      className="flex flex-col rounded-xl border border-border bg-card"
    >
      <div className="border-b border-border px-4 py-3">
        <h2
          id="bexio-contacts-preview-title"
          className="text-sm font-semibold text-foreground"
        >
          Bexio-Kontakte (Vorschau)
        </h2>
      </div>

      {rows.length === 0 ? (
        <p className="p-4 text-sm text-muted-foreground">Keine Kontakte vorhanden.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full table-fixed border-collapse text-xs">
            <thead>
              <tr className="border-b border-border text-left">
                <th scope="col" className="w-[30%] px-2 py-2 text-xs font-medium text-muted-foreground">
                  Name
                </th>
                <th scope="col" className="w-[31%] px-2 py-2 text-xs font-medium text-muted-foreground">
                  E-Mail
                </th>
                <th scope="col" className="w-[10%] whitespace-nowrap px-2 py-2 text-xs font-medium text-muted-foreground">
                  Typ
                </th>
                <th scope="col" className="w-[29%] px-2 py-2 text-xs font-medium text-muted-foreground">
                  Zuordnung
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const status = statuses[c.bexio_contact_id]?.mapping_status;
                return (
                  <tr key={c.id ?? c.bexio_contact_id} className="border-b border-border last:border-0">
                    <td className="px-2 py-2">
                      <div className="truncate text-foreground">{c.display_name || "—"}</div>
                    </td>
                    <td className="px-2 py-2">
                      <div className="truncate text-muted-foreground">{c.mail || "—"}</div>
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-muted-foreground">
                      {c.contact_type || "—"}
                    </td>
                    <td className="px-2 py-2">
                      {status ? (
                        <span
                          className={`inline-flex max-w-full truncate rounded-full border px-1.5 py-0.5 text-[11px] font-medium ${
                            TONE[mappingTone(status)] || TONE.neutral
                          }`}
                        >
                          {mapLabel(MAPPING_STATUS_LABEL, status)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <BexioPreviewFooter
        shown={rows.length}
        total={total}
        noun="Kontakt"
        nounPlural="Kontakte"
        onLoadMore={onLoadMore}
        loadingMore={loadingMore}
      />
    </section>
  );
}
